import React, { useMemo, useRef } from 'react';
import { useQuery } from '@tanstack/react-query';
import { customerGroupingApi } from '../services/apiService';
import { ErrorMessage, LoadingSpinner, HorizontalTableShell, ColumnInfoTooltip } from '../components/ui';
import DashboardSectionAnchor from '../components/ui/DashboardSectionAnchor';
import ChartControls from '../components/ui/ChartControls';
import useChartGuide, { type ChartGuide } from '../hooks/useChartGuide';
import { useBrandBranches } from '../hooks/useBrandQueries';
import { CHART_GUIDES } from '../constants/chartGuides';
import { formatNumber } from '../utils';

interface BrandRef {
  id: number;
  name?: string;
  logo?: string;
}

interface BranchRef {
  id: number;
  name?: string;
  brandId?: number;
}

interface CustomerGroupingDashboardProps {
  selectedBrand?: BrandRef | null;
  selectedBranch?: BranchRef | null;
}

interface PurchaseCountGroup {
  brandId: number;
  purchaseCount: string;
  month1: number;
  month2: number;
  month3: number;
  month6: number;
  month12: number;
  moreThanOneYear: number;
  total: number;
}

interface PurchaseDistanceGroup {
  purchaseCount: string;
  totalCustomers: number;
  minDay: number;
  maxDay: number;
  avgDay: number;
  p25: number;
  p50: number;
  p75: number;
  p90: number;
}

interface CustomerGroupingData {
  brandId: number;
  uniqueYearMonthCount: number;
  purchaseCountGroups: PurchaseCountGroup[];
  purchaseDistanceAllCustomers: PurchaseDistanceGroup[];
  purchaseDistanceLastYear: PurchaseDistanceGroup[];
}

type PercentileKey = 'p25' | 'p50' | 'p75' | 'p90';
type PurchaseDistanceTrend = 'faster' | 'slower' | 'stable';

const PERCENTILE_KEYS: PercentileKey[] = ['p25', 'p50', 'p75', 'p90'];

const PERCENTILE_LABELS: Record<PercentileKey, string> = {
  p25: '۲۵٪',
  p50: '۵۰٪',
  p75: '۷۵٪',
  p90: '۹۰٪',
};

const DAY_DELTA_THRESHOLD = 3;
const PERCENT_DELTA_THRESHOLD = 5;
/** Minimum unique BI_BranchDetails YearMonth values before year-based reports are shown. */
const MIN_COMPARABLE_HISTORY_MONTHS = 12;

interface PercentileComparison {
  allTime: number;
  lastYear: number;
  dayDelta: number;
  percentDelta: number | null;
  trend: PurchaseDistanceTrend;
}

const shouldHidePurchaseDistanceDelta = (
  comparison: PercentileComparison,
  hasComparableYearHistory: boolean,
): boolean => {
  if (!hasComparableYearHistory) return true;
  if (comparison.dayDelta === 0) return true;
  if (comparison.allTime === comparison.lastYear) return true;
  return false;
};

interface PurchaseDistanceComparisonRow {
  purchaseCount: string;
  p25: PercentileComparison | null;
  p50: PercentileComparison | null;
  p75: PercentileComparison | null;
  p90: PercentileComparison | null;
}

interface ReportColumn<T> {
  key: keyof T;
  label: string;
  highlight?: boolean;
  variant?: 'recentYear' | 'inactiveYear' | 'totalCustomers';
  headerTooltip?: string;
  render?: (value: T[keyof T], row: T) => React.ReactNode;
}

const formatCount = (value: unknown) => formatNumber(Number(value || 0));
const formatPercent = (value: number) => `${value.toFixed(1)}%`;
const toPersianDigits = (value: number | string) =>
  String(value).replace(/\d/g, (digit) => '۰۱۲۳۴۵۶۷۸۹'[Number(digit)]);

const formatPurchaseCountLabel = (purchaseCount: string) =>
  purchaseCount.replace('تک خرید', 'یک خرید');

/** فاصله خرید: لیبل گروه نشان‌دهنده شماره خرید هدف است (یک پله بالاتر از تعداد فعلی). */
const formatPurchaseDistanceCountLabel = (purchaseCount: string) => {
  const label = formatPurchaseCountLabel(purchaseCount);
  // این گزارش «خرید بعدی هدف» را یک پله بالاتر از گروه فعلی نشان می‌دهد
  // و خروجی باید با عنوان «خرید دوم/سوم/...» نمایش داده شود.
  if (label.includes('شش خرید به بالا')) return 'خرید هفتم به بالا';
  if (label.includes('پنج خرید')) return 'خرید ششم';
  if (label.includes('چهار خرید')) return 'خرید پنجم';
  if (label.includes('سه خرید')) return 'خرید چهارم';
  if (label.includes('دو خرید')) return 'خرید سوم';
  if (label.includes('یک خرید') || label.includes('تک خرید')) return 'خرید دوم';
  return label;
};

const getNextPurchaseLabel = (purchaseCount: string) => {
  if (purchaseCount.includes('یک خرید') || purchaseCount.includes('تک خرید')) return 'دوم';
  if (purchaseCount.includes('دو خرید')) return 'سوم';
  if (purchaseCount.includes('سه خرید')) return 'چهارم';
  if (purchaseCount.includes('چهار خرید')) return 'پنجم';
  if (purchaseCount.includes('پنج خرید')) return 'ششم';
  if (purchaseCount.includes('شش خرید به بالا')) return 'بعدی';
  return '';
};

interface CellInfoTooltipProps {
  tooltipText: string;
  children: React.ReactNode;
}

const computePercentileComparison = (
  allTime: number,
  lastYear: number,
): PercentileComparison | null => {
  if (allTime <= 0 && lastYear <= 0) return null;

  const dayDelta = lastYear - allTime;
  const percentDelta = allTime > 0 ? (dayDelta / allTime) * 100 : null;

  let trend: PurchaseDistanceTrend = 'stable';
  if (
    dayDelta <= -DAY_DELTA_THRESHOLD
    && (percentDelta === null || percentDelta <= -PERCENT_DELTA_THRESHOLD)
  ) {
    trend = 'faster';
  } else if (
    dayDelta >= DAY_DELTA_THRESHOLD
    && (percentDelta === null || percentDelta >= PERCENT_DELTA_THRESHOLD)
  ) {
    trend = 'slower';
  }

  return { allTime, lastYear, dayDelta, percentDelta, trend };
};

const buildPurchaseDistanceComparisonRows = (
  allCustomers: PurchaseDistanceGroup[],
  lastYearCustomers: PurchaseDistanceGroup[],
): PurchaseDistanceComparisonRow[] => {
  const lastYearByPurchaseCount = new Map(
    lastYearCustomers.map((row) => [row.purchaseCount, row]),
  );

  const purchaseCounts = allCustomers.length > 0
    ? allCustomers.map((row) => row.purchaseCount)
    : lastYearCustomers.map((row) => row.purchaseCount);

  return purchaseCounts.map((purchaseCount) => {
    const allTimeRow = allCustomers.find((row) => row.purchaseCount === purchaseCount);
    const lastYearRow = lastYearByPurchaseCount.get(purchaseCount);

    const percentiles = PERCENTILE_KEYS.reduce((acc, key) => {
      const allTimeValue = Number(allTimeRow?.[key] ?? 0);
      const lastYearValue = Number(lastYearRow?.[key] ?? 0);
      acc[key] = computePercentileComparison(allTimeValue, lastYearValue);
      return acc;
    }, {} as Record<PercentileKey, PercentileComparison | null>);

    return {
      purchaseCount,
      p25: percentiles.p25,
      p50: percentiles.p50,
      p75: percentiles.p75,
      p90: percentiles.p90,
    };
  });
};

const getDeltaTrendClasses = (trend: PurchaseDistanceTrend) => {
  if (trend === 'faster') return 'text-green-600 dark:text-green-400';
  if (trend === 'slower') return 'text-red-600 dark:text-red-400';
  return 'text-gray-500 dark:text-gray-400';
};

const formatSignedDays = (value: number) => {
  const sign = value > 0 ? '+' : value < 0 ? '-' : '';
  return `${sign}${Math.abs(value)}`;
};

const formatSignedPercent = (value: number | null) => {
  if (value === null || value === undefined) return '—';
  const sign = value > 0 ? '+' : value < 0 ? '-' : '';
  return `${sign}${Math.abs(value).toFixed(1)}%`;
};

const getDeltaDirectionLabel = (dayDelta: number) => {
  if (dayDelta < 0) return 'زودتر';
  if (dayDelta > 0) return 'دیرتر';
  return 'تقریباً مشابه';
};

const CellInfoTooltip: React.FC<CellInfoTooltipProps> = ({ tooltipText, children }) => (
  <div className="inline-flex items-center justify-center gap-1.5 whitespace-nowrap leading-none">
    {children}
    <div className="relative group shrink-0">
      <span
        className="inline-flex h-4 w-4 items-center justify-center rounded-full bg-blue-100 text-[10px] font-bold leading-none text-blue-700 dark:bg-blue-900/40 dark:text-blue-300 cursor-help"
        aria-label={tooltipText}
      >
        i
      </span>
      <div className="pointer-events-none absolute z-20 bottom-full right-1/2 mb-2 w-56 translate-x-1/2 rounded-md bg-gray-900 px-3 py-2 text-xs leading-5 text-white opacity-0 shadow-lg transition-opacity group-hover:opacity-100 whitespace-normal">
        {tooltipText}
      </div>
    </div>
  </div>
);

const CustomerGroupingDashboard: React.FC<CustomerGroupingDashboardProps> = ({
  selectedBrand,
  selectedBranch,
}) => {
  const brandId = selectedBrand?.id || selectedBranch?.brandId;
  const branchId = selectedBranch?.id;

  const { data: brandBranches } = useBrandBranches(brandId ?? null);
  const isMultiBranch = (brandBranches?.length ?? 0) > 1;

  const labels = useMemo(() => {
    if (isMultiBranch) {
      return {
        pageTitle: 'گروه‌بندی مشتریان برند',
        pageSubtitle: 'این گزارش بر اساس کل شعب برند انتخاب‌شده محاسبه می‌شود.',
        totalCustomers: 'مجموع مشتریان',
        recentMonthCustomers: 'مشتریان یک ماه اخیر',
        recentYearCustomers: 'مشتریان یک سال اخیر',
        inactiveMoreThanYear: 'مشتریان غیرفعال در یک سال گذشته',
        purchaseCountTitle: 'گروه‌بندی بر اساس تعداد خرید در سطح برند',
        purchaseCountDescription: 'توزیع مشتریان کل شعب برند بر اساس تعداد خرید و زمان آخرین سفارش. درصد هر سلول = سهم آن گروه از مجموع همان ستون.',
        distanceLastYearTitle: 'فاصله خرید مشتریان برند — یک سال اخیر',
        distanceLastYearDescription: 'شاخص‌های توصیفی فاصله خرید برای مشتریان فعال برند در یک سال اخیر',
        distanceAllTitle: 'فاصله خرید کل مشتریان برند',
        distanceAllDescription: 'شاخص‌های توصیفی فاصله خرید برای همه مشتریان برند (کل شعب)',
        comparisonTitle: 'مقایسه ریتم بازگشت برند: یک سال اخیر در برابر کل دوره',
        comparisonDescription: 'تفاوت فاصله خرید مجدد در سطح برند بین مشتریان فعال یک سال اخیر و کل دوره — منفی یعنی فاصله خرید مشتریان کم می شود و مثبت یعنی فاصله خرید افزایش می یابد ',
        totalCustomersTooltip: 'کل مشتریان برند',
      };
    }

    return {
      pageTitle: 'گروه‌بندی مشتریان',
      pageSubtitle: null as string | null,
      totalCustomers: 'مجموع مشتریان',
      recentMonthCustomers: 'مشتریان یک ماه اخیر',
      recentYearCustomers: 'مشتریان یک سال اخیر',
      inactiveMoreThanYear: 'مشتریان غیرفعال در یک سال گذشته',
      purchaseCountTitle: 'گروه‌بندی بر اساس تعداد خرید',
      purchaseCountDescription: 'توزیع مشتریان بر اساس تعداد خرید و زمان آخرین سفارش. درصد هر سلول = سهم آن گروه از مجموع همان ستون.',
      distanceLastYearTitle: 'فاصله خرید کل مشتریان یک سال اخیر',
      distanceLastYearDescription: 'شاخص‌های توصیفی فاصله خرید برای مشتریان فعال در یک سال اخیر',
      distanceAllTitle: 'فاصله خرید کل مشتریان',
      distanceAllDescription: 'شاخص‌های توصیفی فاصله خرید برای همه مشتریان',
      comparisonTitle: 'مقایسه ریتم بازگشت: یک سال اخیر در برابر کل دوره',
      comparisonDescription: 'تفاوت فاصله خرید مجدد بین مشتریان فعال یک سال اخیر و کل دوره — منفی یعنی فاصله خرید مشتریان کم می شود و مثبت یعنی فاصله خرید افزایش می یابد ',
      totalCustomersTooltip: 'کل مشتریان',
    };
  }, [isMultiBranch]);

  const {
    data,
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: ['customerGroupingDashboard', brandId, branchId],
    queryFn: async () => {
      if (!brandId) throw new Error('Brand ID is required');
      const response = await customerGroupingApi.getDashboardData(brandId, branchId);
      return response.data as CustomerGroupingData;
    },
    enabled: Boolean(brandId),
    staleTime: 5 * 60 * 1000,
    retry: 1,
  });

  const purchaseRows = data?.purchaseCountGroups || [];

  const purchaseColumnTotals = useMemo(() => {
    return purchaseRows.reduce(
      (totals, row) => ({
        month1: totals.month1 + row.month1,
        month2: totals.month2 + row.month2,
        month3: totals.month3 + row.month3,
        month6: totals.month6 + row.month6,
        month12: totals.month12 + row.month12,
        moreThanOneYear: totals.moreThanOneYear + row.moreThanOneYear,
        total: totals.total + row.total,
      }),
      {
        month1: 0,
        month2: 0,
        month3: 0,
        month6: 0,
        month12: 0,
        moreThanOneYear: 0,
        total: 0,
      },
    );
  }, [purchaseRows]);

  const uniqueYearMonthCount = data?.uniqueYearMonthCount ?? 0;

  const hasFullYearOfBranchDetails =
    uniqueYearMonthCount > MIN_COMPARABLE_HISTORY_MONTHS;

  const renderPurchaseValueWithPercent = (
    key: keyof typeof purchaseColumnTotals,
    periodLabel: string,
  ) => (
    value: PurchaseCountGroup[keyof PurchaseCountGroup],
    row: PurchaseCountGroup,
  ) => {
    const numericValue = Number(value || 0);
    const columnTotal = purchaseColumnTotals[key];

    if (row.purchaseCount === 'مجموع') {
      return (
        <span className="font-extrabold">{formatCount(numericValue)}</span>
      );
    }

    const percent = columnTotal > 0 ? (numericValue / columnTotal) * 100 : 0;
    const purchaseLabel = formatPurchaseCountLabel(row.purchaseCount);
    const tooltipText = `درصد = سهم این گروه از مجموع ستون «${periodLabel}». از ${formatCount(columnTotal)} مشتری این ستون، ${formatCount(numericValue)} نفر (${toPersianDigits(percent.toFixed(1))}٪) در گروه «${purchaseLabel}» هستند.`;
    return (
      <CellInfoTooltip tooltipText={tooltipText}>
        <div className="flex flex-col items-center gap-0.5 leading-tight tabular-nums">
          <span className="font-semibold text-text-primary">{formatCount(numericValue)}</span>
          <span className="text-xs font-medium text-blue-600 dark:text-blue-300">
            {formatPercent(percent)}
          </span>
        </div>
      </CellInfoTooltip>
    );
  };

  const getPurchaseColumnPercentTooltip = (periodLabel: string) =>
    `درصد هر سلول = تعداد مشتریان این گروه ÷ مجموع مشتریان ستون «${periodLabel}» × ۱۰۰`;

  const purchaseTotalsRow: PurchaseCountGroup = useMemo(() => ({
    brandId: brandId ?? 0,
    purchaseCount: 'مجموع',
    ...purchaseColumnTotals,
  }), [brandId, purchaseColumnTotals]);

  const purchaseColumns: ReportColumn<PurchaseCountGroup>[] = useMemo(() => [
    {
      key: 'purchaseCount',
      label: 'تعداد خرید',
      highlight: true,
      render: (value) => formatPurchaseCountLabel(String(value)),
    },
    {
      key: 'month1',
      label: 'یک ماه اخیر',
      headerTooltip: getPurchaseColumnPercentTooltip('یک ماه اخیر'),
      render: renderPurchaseValueWithPercent('month1', 'یک ماه اخیر'),
    },
    {
      key: 'month2',
      label: 'دو ماه اخیر',
      headerTooltip: getPurchaseColumnPercentTooltip('دو ماه اخیر'),
      render: renderPurchaseValueWithPercent('month2', 'دو ماه اخیر'),
    },
    {
      key: 'month3',
      label: 'سه ماه اخیر',
      headerTooltip: getPurchaseColumnPercentTooltip('سه ماه اخیر'),
      render: renderPurchaseValueWithPercent('month3', 'سه ماه اخیر'),
    },
    {
      key: 'month6',
      label: 'شش ماه اخیر',
      headerTooltip: getPurchaseColumnPercentTooltip('شش ماه اخیر'),
      render: renderPurchaseValueWithPercent('month6', 'شش ماه اخیر'),
    },
    {
      key: 'month12',
      label: 'یک سال اخیر',
      variant: 'recentYear',
      headerTooltip: getPurchaseColumnPercentTooltip('یک سال اخیر'),
      render: renderPurchaseValueWithPercent('month12', 'یک سال اخیر'),
    },
    {
      key: 'moreThanOneYear',
      label: 'مشتریان غیرفعال در یک سال گذشته',
      variant: 'inactiveYear',
      headerTooltip: getPurchaseColumnPercentTooltip('غیرفعال در یک سال گذشته'),
      render: renderPurchaseValueWithPercent('moreThanOneYear', 'غیرفعال در یک سال گذشته'),
    },
    {
      key: 'total',
      label: 'مجموع',
      highlight: true,
      variant: 'totalCustomers',
      headerTooltip: getPurchaseColumnPercentTooltip(labels.totalCustomersTooltip),
      render: renderPurchaseValueWithPercent('total', labels.totalCustomersTooltip),
    },
  ], [purchaseColumnTotals, labels.totalCustomersTooltip]);

  const renderDistanceValueWithInfo = (
    percentileLabel: '25' | '50' | '75' | '90',
  ) => (
    value: PurchaseDistanceGroup[keyof PurchaseDistanceGroup],
    row: PurchaseDistanceGroup,
  ) => {
    const numericValue = Number(value || 0);
    const nextPurchaseLabel = getNextPurchaseLabel(row.purchaseCount);
    const tooltipText = `${toPersianDigits(percentileLabel)}% مشتریان ، خرید ${nextPurchaseLabel} خود را حداکثر طی ${toPersianDigits(numericValue)} روز انجام میدهند`;
    return (
      <CellInfoTooltip tooltipText={tooltipText}>
        <span>{formatCount(numericValue)}</span>
      </CellInfoTooltip>
    );
  };

  const distanceColumns: ReportColumn<PurchaseDistanceGroup>[] = useMemo(() => [
    {
      key: 'purchaseCount',
      label: 'فاصله خرید',
      highlight: true,
      render: (value) => formatPurchaseDistanceCountLabel(String(value)),
    },
    { key: 'p25', label: '۲۵%', render: renderDistanceValueWithInfo('25') },
    { key: 'p50', label: '۵۰%', render: renderDistanceValueWithInfo('50') },
    { key: 'p75', label: '۷۵%', render: renderDistanceValueWithInfo('75') },
    { key: 'p90', label: '۹۰%', render: renderDistanceValueWithInfo('90') },
  ], []);

  const purchaseDistanceComparisonRows = useMemo(
    () => buildPurchaseDistanceComparisonRows(
      data?.purchaseDistanceAllCustomers || [],
      data?.purchaseDistanceLastYear || [],
    ),
    [data?.purchaseDistanceAllCustomers, data?.purchaseDistanceLastYear],
  );

  const renderComparisonValueWithInfo = (
    percentileLabel: '25' | '50' | '75' | '90',
  ) => (
    value: PurchaseDistanceComparisonRow[keyof PurchaseDistanceComparisonRow],
    row: PurchaseDistanceComparisonRow,
  ) => {
    const comparison = value as PercentileComparison | null;
    if (!comparison) {
      return <span className="text-gray-400">—</span>;
    }

    if (shouldHidePurchaseDistanceDelta(comparison, true)) {
      return <span className="text-gray-400">—</span>;
    }

    const directionLabel = getDeltaDirectionLabel(comparison.dayDelta);
    const tooltipText = `${toPersianDigits(percentileLabel)}% مشتریان ${formatPurchaseDistanceCountLabel(row.purchaseCount)} خود را در یک سال اخیر حداکثر طی ${toPersianDigits(comparison.lastYear)} روز (در کل دوره ${toPersianDigits(comparison.allTime)} روز) انجام داده‌اند — ${toPersianDigits(Math.abs(comparison.dayDelta))} روز ${directionLabel}`;

    return (
      <CellInfoTooltip tooltipText={tooltipText}>
        <div className="flex flex-col items-center gap-1 leading-tight">
          <span className={`font-semibold ${getDeltaTrendClasses(comparison.trend)}`}>
            <span dir="ltr" className="tabular-nums">{formatSignedDays(comparison.dayDelta)}</span>
            {' '}روز
          </span>
          <span
            dir="ltr"
            className={`text-xs font-medium tabular-nums ${getDeltaTrendClasses(comparison.trend)}`}
          >
            {formatSignedPercent(comparison.percentDelta)}
          </span>
        </div>
      </CellInfoTooltip>
    );
  };

  const comparisonColumns: ReportColumn<PurchaseDistanceComparisonRow>[] = useMemo(() => [
    {
      key: 'purchaseCount',
      label: 'فاصله خرید',
      highlight: true,
      render: (value) => formatPurchaseDistanceCountLabel(String(value)),
    },
    { key: 'p25', label: '۲۵%', render: renderComparisonValueWithInfo('25') },
    { key: 'p50', label: '۵۰%', render: renderComparisonValueWithInfo('50') },
    { key: 'p75', label: '۷۵%', render: renderComparisonValueWithInfo('75') },
    { key: 'p90', label: '۹۰%', render: renderComparisonValueWithInfo('90') },
  ], []);

  const summary = useMemo(() => {
    const rows = data?.purchaseCountGroups || [];
    return {
      totalCustomers: rows.reduce((sum, row) => sum + row.total, 0),
      recentMonthCustomers: rows.reduce((sum, row) => sum + row.month1, 0),
      recentYearCustomers: rows.reduce((sum, row) => sum + row.month12, 0),
      inactiveMoreThanYear: rows.reduce((sum, row) => sum + row.moreThanOneYear, 0),
    };
  }, [data]);

  if (!brandId) {
    return (
      <div dir="rtl" className="container-dashboard">
        <div className="card-base card-padding border border-status-warning" role="alert">
          برای دیدن این داشبورد، ابتدا برند و شعبه را انتخاب کنید.
        </div>
      </div>
    );
  }

  if (isLoading) {
    return <LoadingSpinner variant="dashboard" message="در حال بارگذاری گروه‌بندی مشتریان…" />;
  }

  if (error) {
    return (
      <div dir="rtl" className="container-dashboard">
        <div className="card-base card-padding">
          <ErrorMessage
            message="خطا در دریافت اطلاعات گروه‌بندی مشتریان"
            onRetry={() => refetch()}
          />
        </div>
      </div>
    );
  }

  return (
    <div dir="rtl" className="container-dashboard">
      <div className="space-y-8 md:space-y-10 animate-enter-up">
        <div className="text-center">
          <h1 className="title-dashboard">{labels.pageTitle}</h1>
          {labels.pageSubtitle && (
            <p className="text-sm text-text-secondary mt-2">
              {labels.pageSubtitle}
            </p>
          )}
        </div>

        <DashboardSectionAnchor sectionId="customer-grouping-summary">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <SummaryCard title={labels.totalCustomers} value={summary.totalCustomers} tone="cyan" />
            <SummaryCard title={labels.recentMonthCustomers} value={summary.recentMonthCustomers} tone="green" />
            <SummaryCard title={labels.recentYearCustomers} value={summary.recentYearCustomers} tone="emerald" />
            <SummaryCard title={labels.inactiveMoreThanYear} value={summary.inactiveMoreThanYear} tone="warning" />
          </div>
        </DashboardSectionAnchor>

        <DashboardSectionAnchor sectionId="customer-grouping-purchase-count">
        <ReportTable
          title={labels.purchaseCountTitle}
          description={labels.purchaseCountDescription}
          columns={purchaseColumns}
          rows={purchaseRows}
          totalsRow={purchaseRows.length > 0 ? purchaseTotalsRow : undefined}
          minWidth="1100px"
          componentId="customer-grouping-purchase-count"
          guide={CHART_GUIDES.purchaseCountGroupingTable}
        />
        </DashboardSectionAnchor>

        {hasFullYearOfBranchDetails && (
          <DashboardSectionAnchor sectionId="customer-grouping-distance-last-year">
          <ReportTable
            title={labels.distanceLastYearTitle}
            description={labels.distanceLastYearDescription}
            columns={distanceColumns}
            rows={data?.purchaseDistanceLastYear || []}
            minWidth="1050px"
            componentId="customer-grouping-distance-last-year"
            guide={CHART_GUIDES.purchaseDistanceLastYearTable}
          />
          </DashboardSectionAnchor>
        )}

        <DashboardSectionAnchor sectionId="customer-grouping-distance-all">
        <ReportTable
          title={labels.distanceAllTitle}
          description={labels.distanceAllDescription}
          columns={distanceColumns}
          rows={data?.purchaseDistanceAllCustomers || []}
          minWidth="1050px"
          componentId="customer-grouping-distance-all"
          guide={CHART_GUIDES.purchaseDistanceAllCustomersTable}
        />
        </DashboardSectionAnchor>

        {hasFullYearOfBranchDetails && (
          <DashboardSectionAnchor sectionId="customer-grouping-distance-comparison">
          <ReportTable
            title={labels.comparisonTitle}
            description={labels.comparisonDescription}
            columns={comparisonColumns}
            rows={purchaseDistanceComparisonRows}
            minWidth="1050px"
            componentId="customer-grouping-distance-comparison"
            guide={CHART_GUIDES.purchaseDistanceComparisonTable}
          />
          </DashboardSectionAnchor>
        )}
      </div>
    </div>
  );
};

interface SummaryCardProps {
  title: string;
  value: number;
  tone: 'cyan' | 'green' | 'emerald' | 'warning';
}

const SummaryCard: React.FC<SummaryCardProps> = ({ title, value, tone }) => {
  const toneStyle = {
    cyan: 'var(--accent-cyan)',
    green: 'var(--accent-green)',
    emerald: '#059669',
    warning: 'var(--status-warning)',
  }[tone];

  return (
    <div className="card-base card-padding animate-scale-in">
      <div className="text-sm font-medium text-text-secondary text-center mb-3">{title}</div>
      <div className="text-3xl font-bold text-center" style={{ color: toneStyle }}>
        {formatNumber(value)}
      </div>
    </div>
  );
};

interface ReportTableProps<T extends object> {
  title: string;
  description: string;
  columns: ReportColumn<T>[];
  rows: T[];
  totalsRow?: T;
  minWidth: string;
  componentId: string;
  guide: ChartGuide;
}

const ReportTable = <T extends object>({
  title,
  description,
  columns,
  rows,
  totalsRow,
  minWidth,
  componentId,
  guide,
}: ReportTableProps<T>) => {
  const chartRef = useRef<HTMLDivElement>(null);
  const { openGuide } = useChartGuide();

  const getHeaderVariantClasses = (variant?: ReportColumn<T>['variant']) => {
    if (variant === 'recentYear') return '!bg-emerald-100 dark:!bg-emerald-900/30 !text-emerald-800 dark:!text-emerald-200';
    if (variant === 'inactiveYear') return '!bg-gray-200 dark:!bg-gray-700/70 !text-gray-700 dark:!text-gray-200';
    if (variant === 'totalCustomers') return '!bg-cyan-100 dark:!bg-cyan-900/30 !text-cyan-900 dark:!text-cyan-200';
    return '';
  };

  const getCellVariantClasses = (variant?: ReportColumn<T>['variant']) => {
    if (variant === 'recentYear') return '!bg-emerald-50 dark:!bg-emerald-900/20 font-bold text-emerald-700 dark:text-emerald-200';
    if (variant === 'inactiveYear') return '!bg-gray-100 dark:!bg-gray-800/80 font-bold text-gray-700 dark:text-gray-200';
    if (variant === 'totalCustomers') return '!bg-cyan-50 dark:!bg-cyan-900/20 font-extrabold text-cyan-800 dark:text-cyan-200';
    return '';
  };

  return (
    <div ref={chartRef} className="relative card-base card-padding animate-enter animate-delay-3">
      <ChartControls
        componentId={componentId}
        componentTitle={title}
        componentType="table"
        dashboardSource="customer-grouping"
        onInfo={() => openGuide(guide)}
        chartRef={chartRef}
        size="md"
        className="mb-4"
      />
      <h3 className="title-card">{title}</h3>
      <p className="text-sm text-text-secondary mt-1 text-center">{description}</p>

      <div className="mt-6">
        <HorizontalTableShell minWidth={minWidth}>
            <table className="w-full border-collapse" style={{ direction: 'rtl' }}>
              <thead>
                <tr className="border-b-2 !bg-gray-100 dark:!bg-gray-800" style={{ borderColor: 'var(--border-secondary)' }}>
                  {columns.map((column) => (
                    <th
                      key={String(column.key)}
                      className={`px-3 py-4 text-center text-xs font-bold uppercase tracking-wider border-l last:border-l-0 align-middle ${getHeaderVariantClasses(column.variant)}`}
                      style={{
                        color: 'var(--text-primary)',
                        borderColor: 'var(--border-primary)',
                      }}
                    >
                      <div className="flex flex-wrap items-center justify-center gap-x-1.5 gap-y-1 font-semibold text-sm normal-case tracking-normal leading-snug">
                        <span>{column.label}</span>
                        {column.headerTooltip && <ColumnInfoTooltip text={column.headerTooltip} />}
                      </div>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.map((row, rowIndex) => (
                  <tr
                    key={`${title}-${rowIndex}`}
                    className={`transition-all border-b ${rowIndex % 2 === 0 ? '!bg-white dark:!bg-gray-900' : '!bg-gray-50 dark:!bg-gray-800'} hover:!bg-blue-50 dark:hover:!bg-gray-700`}
                    style={{ borderColor: 'var(--border-primary)' }}
                  >
                    {columns.map((column) => {
                      const value = row[column.key];
                      return (
                        <td
                          key={String(column.key)}
                          className={`px-3 py-4 text-center text-sm border-l last:border-l-0 transition-colors align-middle ${column.highlight ? 'font-bold text-text-primary bg-bg-tertiary' : 'font-medium text-text-secondary'} ${getCellVariantClasses(column.variant)}`}
                          style={{ borderColor: 'var(--border-primary)' }}
                        >
                          {column.render ? column.render(value, row) : String(value)}
                        </td>
                      );
                    })}
                  </tr>
                ))}

                {totalsRow && (
                  <tr
                    className="border-t-2 bg-primary-100 dark:bg-primary-900/30 font-bold"
                    style={{ borderColor: 'var(--border-secondary)' }}
                  >
                    {columns.map((column) => {
                      const value = totalsRow[column.key];
                      return (
                        <td
                          key={`total-${String(column.key)}`}
                          className={`px-3 py-4 text-center text-sm font-bold border-l last:border-l-0 text-primary-700 dark:text-primary-300 align-middle ${getCellVariantClasses(column.variant)}`}
                          style={{ borderColor: 'var(--border-primary)' }}
                        >
                          {column.render ? column.render(value, totalsRow) : String(value)}
                        </td>
                      );
                    })}
                  </tr>
                )}

                {rows.length === 0 && (
                  <tr>
                    <td colSpan={columns.length} className="px-6 py-8 text-center text-text-secondary">
                      داده‌ای برای نمایش وجود ندارد.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
        </HorizontalTableShell>
      </div>
    </div>
  );
};

export default CustomerGroupingDashboard;
